import { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { User, LogOut, Mail, Lock, Loader2, CheckCircle } from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';
import { MemberArea } from '../components/member/MemberArea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors";

export default function AccountPage() {
  const { user, login, register, logout } = useAuth();
  const [mode, setMode] = useState('login'); // login | register | forgot
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else if (mode === 'register') {
        await register(email, password);
      } else {
        const res = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
        setInfo(res.data.message);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  if (user === null) {
    return (
      <div className="min-h-[60vh] bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#C41E3A]" size={32} />
      </div>
    );
  }

  if (user) {
    return <MemberArea user={user} logout={logout} />;
  }

  return (
    <div className="min-h-[60vh] bg-[#050505] py-16 px-4" data-testid="account-page">
      <div className="max-w-md mx-auto bg-[#0A0A0A] border border-[#27272A] rounded-sm p-8">
        <h1 className="font-['Oswald'] text-2xl uppercase tracking-wide text-white text-center mb-2">
          {mode === 'login' ? 'Member Sign In' : mode === 'register' ? 'Create Account' : 'Reset Password'}
        </h1>
        <p className="text-gray-400 text-xs text-center mb-6">
          {mode === 'register'
            ? 'Creating an account automatically subscribes you to members-only deals & promo codes.'
            : mode === 'forgot'
            ? "Enter your email and we'll send you a reset link."
            : 'Sign in with the email you subscribed with.'}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input type="email" required placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} className={`${inputCls} pl-9`} data-testid="auth-email-input" />
          </div>
          {mode !== 'forgot' && (
            <div className="relative">
              <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input type="password" required minLength={6} placeholder={mode === 'register' ? 'Choose a password (6+ characters)' : 'Password'} value={password} onChange={e => setPassword(e.target.value)} className={`${inputCls} pl-9`} data-testid="auth-password-input" />
            </div>
          )}
          {error && <p className="text-[#C41E3A] text-sm" data-testid="auth-error">{error}</p>}
          {info && (
            <p className="text-green-400 text-sm flex items-start gap-2" data-testid="auth-info">
              <CheckCircle size={15} className="mt-0.5 flex-shrink-0" /> {info}
            </p>
          )}
          <button type="submit" disabled={busy} className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white font-bold uppercase tracking-wider py-3.5 rounded-sm transition-colors" data-testid="auth-submit-btn">
            {busy ? 'Please wait...' : mode === 'login' ? 'Sign In' : mode === 'register' ? 'Create Account' : 'Send Reset Link'}
          </button>
        </form>
        <div className="mt-5 text-center space-y-2">
          {mode === 'login' && (
            <>
              <button onClick={() => { setMode('forgot'); setError(''); setInfo(''); }} className="block w-full text-gray-500 hover:text-[#D4AF37] text-xs transition-colors" data-testid="forgot-password-link">
                Forgot your password?
              </button>
              <button onClick={() => { setMode('register'); setError(''); setInfo(''); }} className="block w-full text-gray-400 hover:text-white text-sm transition-colors" data-testid="switch-to-register">
                New here? <span className="text-[#C41E3A] font-semibold">Create an account</span>
              </button>
            </>
          )}
          {mode !== 'login' && (
            <button onClick={() => { setMode('login'); setError(''); setInfo(''); }} className="block w-full text-gray-400 hover:text-white text-sm transition-colors" data-testid="switch-to-login">
              Already a member? <span className="text-[#C41E3A] font-semibold">Sign in</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
